import { Box, Stack, useMediaQuery, useTheme } from "@mui/material";
import { useLayoutEffect, useRef, useState } from "react";
import { Card, Typography, palette, Tooltip } from "@powerdiary/components-library/lib";
import { type LucideIcon } from "lucide-react";
import styles from "./ConfirmationDetailCard.module.css";

export interface ConfirmationDetailCardProps {
    title: string;
    content: string;
    icon: LucideIcon;
    subContent?: string | null;
    contentSuffix?: string | null;
}

export default function ConfirmationDetailCard({
    title,
    content,
    icon: Icon,
    subContent,
    contentSuffix,
}: Readonly<ConfirmationDetailCardProps>) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const contentRef = useRef<HTMLSpanElement | null>(null);
    const [isOverflowing, setIsOverflowing] = useState(false);

    useLayoutEffect(() => {
        if (!contentRef.current) return;
        const { scrollWidth, clientWidth } = contentRef.current;
        setIsOverflowing(scrollWidth > clientWidth);
    }, [content, isMobile]);

    return (
        <Card
            className={styles.wrapper}
            sx={{ backgroundColor: palette.neutral["50"], padding: "12px" }}
            data-test-id={`section_confirmation_detail_card_${title}`}
        >
            <Box
                className={styles.contentWrapper}
                sx={{
                    [theme.breakpoints.down("sm")]: {
                        gap: "10px",
                    },
                }}
            >
                <Box className={styles.iconContainer} data-test-id={`icon_confirmation_detail_card_${title}`}>
                    <Icon
                        className={styles.icon}
                        size={isMobile ? 16 : 20}
                        strokeWidth={2}
                        color={theme.palette.primary.main}
                        aria-hidden
                    />
                </Box>

                <Box className={styles.textContainer}>
                    <Typography
                        variant="paragraphSmallRegular"
                        className={styles.title}
                        data-test-id={`text_confirmation_detail_card_title_${title}`}
                    >
                        {title}
                    </Typography>

                    <Stack direction="row" alignItems="baseline" spacing={0.5} sx={{ minWidth: 0 }}>
                        <Tooltip title={isOverflowing ? content : ""} disableHoverListener={!isOverflowing}>
                            <Typography
                                variant="paragraphMediumSemiBold"
                                color="textPrimary"
                                className={styles.content}
                                data-test-id={`text_confirmation_detail_card_content_${title}`}
                                ref={contentRef}
                            >
                                {content}
                            </Typography>
                        </Tooltip>

                        {contentSuffix && (
                            <Typography
                                component="span"
                                variant="paragraphSmallRegular"
                                color="textSecondary"
                                data-test-id={`text_confirmation_detail_card_suffix_${title}`}
                                sx={{ flexShrink: 0 }}
                            >
                                {contentSuffix}
                            </Typography>
                        )}
                    </Stack>

                    {subContent && (
                        <Typography
                            variant="paragraphSmallRegular"
                            color="textSecondary"
                            data-test-id={`text_confirmation_detail_card_subcontent_${title}`}
                        >
                            {subContent}
                        </Typography>
                    )}
                </Box>
            </Box>
        </Card>
    );
}
